/**
 * QA-398 — 펀딩 캠페인 목록 화면 타입·검색 조건. 검색·정렬은 서버(settlement #9)가 한다(useFundingRows).
 */
import type { FundingStatusEnum, Money } from '@/lib/api-admin/types';

export const FUNDING_CATEGORY_VALUES = ['ALBUM', 'MD', 'LIGHT_STICK', 'ETC'] as const;
export type FundingCategory = (typeof FUNDING_CATEGORY_VALUES)[number];

export const FUNDING_STATUS_VALUES: ReadonlyArray<FundingStatusEnum> = [
  'DRAFT',
  'ACTIVE',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'SETTLED',
];

/** 화면 한 줄 — 목록 응답 + 상세에서 채운 값. */
export interface FundingRow {
  campaignId: string;
  title: string;
  status: FundingStatusEnum;
  artistId?: string | null;
  artistName?: string | null;
  category?: string | null;
  goalAmount?: Money | null;
  currentAmount?: Money | null;
  achievementRate?: number | null;
  pledgeCount?: number | null;
  rewardCount?: number | null;
  startAt?: string | null;
  endAt?: string | null;
  launchedAt?: string | null;
  closedAt?: string | null;
  createdAt?: string | null;
}

/** 공개 여부 — 작성중(DRAFT)만 비공개, 런칭된 뒤(진행중·달성·실패·관리자 취소·정산 완료)는 공개. */
export const isFundingPublic = (status: FundingStatusEnum): boolean => status !== 'DRAFT';

export type FundingVisibility = 'ALL' | 'PUBLIC' | 'PRIVATE';

export interface FundingListFilter {
  keyword: string;
  statuses: ReadonlyArray<FundingStatusEnum>;
  artistId: string | null;
  category: FundingCategory | '';
  visibility: FundingVisibility;
  /** 펀딩 기간 — 캠페인 기간(시작~종료)이 이 범위와 겹치면 포함. yyyy-MM-dd(KST). */
  periodFrom: string;
  periodTo: string;
  /** 런칭일·마감일 — 그날(KST) 범위. */
  launchedFrom: string;
  launchedTo: string;
  closedFrom: string;
  closedTo: string;
}

export const EMPTY_FUNDING_FILTER: FundingListFilter = {
  keyword: '',
  statuses: [],
  artistId: null,
  category: '',
  visibility: 'ALL',
  periodFrom: '',
  periodTo: '',
  launchedFrom: '',
  launchedTo: '',
  closedFrom: '',
  closedTo: '',
};

const KST_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });

/** ISO 시각 → KST 날짜(yyyy-MM-dd). 없거나 잘못되면 ''. */
export const kstDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : KST_DATE.format(d);
};

export type FundingSortKey = 'achievementRate' | 'launchedAt' | 'closedAt' | 'createdAt';
export interface FundingListSort {
  key: FundingSortKey;
  direction: 'asc' | 'desc';
}

